import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trainingRequestsAPI, partnersAPI, usersAPI, analyticsAPI, purchaseOrdersAPI, sessionsAPI, trainersAPI, feedbackAPI, taxonomyAPI, preferencesAPI } from '../services/api';
import { ColumnFilterPopover } from '../components/shared/ColumnFilterPopover';
import { ActiveFiltersBar } from '../components/shared/ActiveFiltersBar';
import { TablePagination, PAGE_SIZE } from '../components/shared/TablePagination';
import { ColumnsPicker } from '../components/shared/ColumnsPicker';
import { DateRangeFilterPopover } from '../components/shared/DateRangeFilterPopover';
import { FiscalYearSelect } from '../components/shared/FiscalYearSelect';
import { POAlertBanners } from '../components/dashboard/POAlertBanners';
import { ProjectStatusPieChart } from '../components/dashboard/ProjectStatusPieChart';
import { TrainingsByPartnerChart } from '../components/dashboard/TrainingsByPartnerChart';
import { MonthlyTrendChart } from '../components/dashboard/MonthlyTrendChart';
import { OwnerBreakdownChart } from '../components/dashboard/OwnerBreakdownChart';
import { DashboardDrillThroughPanel } from '../components/dashboard/DashboardDrillThroughPanel';
import { FeedbackHistoryPanel } from '../components/dashboard/FeedbackHistoryPanel';
import { KpiTrendBadge } from '../components/dashboard/KpiTrendBadge';
import { FeedbackTrendCard } from '../components/dashboard/FeedbackTrendCard';
import { UpcomingDeliveriesCard } from '../components/dashboard/UpcomingDeliveriesCard';
import { AttendanceFillRateCard } from '../components/dashboard/AttendanceFillRateCard';
import { MonthlyConsumptionChart } from '../components/purchase-orders/MonthlyConsumptionChart';
import { ProjectStatusCalendar } from '../components/private-deliveries/ProjectStatusCalendar';
import { sessionTrainers } from '../components/shared/calendar/calendarUtils';
import { PrivateDeliveryTrainersTab } from '../components/private-deliveries/PrivateDeliveryTrainersTab';
import { AllSessionsPage } from './AllSessionsPage';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Eye, 
  Edit2, 
  Trash2,
  Building2,
  Calendar,
  Users,
  Filter,
  FolderKanban,
  Copy,
  CheckSquare,
  GraduationCap,
  CalendarDays,
  Star,
  MapPin,
  LayoutDashboard,
  CalendarRange,
  Download,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  PieChart, Pie, Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

const STATUS_VARIANTS = {
  requested: { label: 'Requested', className: 'bg-yellow-100 text-yellow-800' },
  planning: { label: 'Planning', className: 'bg-slate-100 text-slate-800' },
  scheduled: { label: 'Scheduled', className: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'In Progress', className: 'bg-violet-100 text-violet-800' },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
};

const PROJECT_CORE_COLUMNS = [
  { key: 'partner', label: 'Partner' },
  { key: 'requestor', label: 'Requestor' },
  { key: 'pm', label: 'Project Manager' },
  { key: 'sessions', label: 'No. of Sessions' },
  { key: 'amount_invoiced', label: 'Amount Invoiced' },
  { key: 'session_date', label: 'Session Date' },
  { key: 'status', label: 'Status' },
];

export const TrainingProjectsPage = () => {
  const { user, isPM } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [projPage, setProjPage] = useState(1);
  // Header-based column filters (multi-select checklists + date range)
  const [colFilters, setColFilters] = useState({
    project: new Set(), partner: new Set(), requestor: new Set(), pm: new Set(), status: new Set(),
  });
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  // Tab control (Projects | Dashboard | Calendar)
  const [activeTab, setActiveTab] = useState('projects');
  // Dashboard analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [poAlerts, setPoAlerts] = useState([]);
  const [pdTimePeriod, setPdTimePeriod] = useState('all');
  const [pdFiscalYear, setPdFiscalYear] = useState('all');
  const [previousAnalyticsSummary, setPreviousAnalyticsSummary] = useState(null);
  // Calendar
  const [calendarSessions, setCalendarSessions] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const sessionCountByProject = useMemo(() => {
    const map = {};
    calendarSessions.forEach(s => { map[s.training_project_id] = (map[s.training_project_id] || 0) + 1; });
    return map;
  }, [calendarSessions]);
  const amountInvoicedByProject = useMemo(() => {
    const map = {};
    calendarSessions.forEach(s => {
      map[s.training_project_id] = (map[s.training_project_id] || 0) + (parseFloat(s.amount_invoiced) || 0);
    });
    return map;
  }, [calendarSessions]);
  // Trainers tab (scoped to Private Deliveries)
  const [pdTrainersAll, setPdTrainersAll] = useState([]);
  const [pdTrainersLoading, setPdTrainersLoading] = useState(false);
  const [pdTrainersLoaded, setPdTrainersLoaded] = useState(false);
  // Dashboard drill-through (inline, stays within the Dashboard tab)
  const [drillThrough, setDrillThrough] = useState(null);
  // Upcoming Deliveries window
  const [upcomingWindowWeeks, setUpcomingWindowWeeks] = useState(4);
  // Month-wise Consumed/Invoiced budget, scoped to Private Deliveries
  const [pdMonthlyConsumption, setPdMonthlyConsumption] = useState(null);
  // Column visibility (Projects table) + taxonomy-driven custom field columns
  const [taxonomyCategories, setTaxonomyCategories] = useState([]);
  const [visibleProjectCols, setVisibleProjectCols] = useState(PROJECT_CORE_COLUMNS.map(c => c.key));
  const customProjectColumns = taxonomyCategories.map(c => ({ key: `custom:${c.id}`, taxonomyKey: c.id, label: c.label }));

  useEffect(() => {
    taxonomyAPI.getAll().then(setTaxonomyCategories).catch(() => {});
    preferencesAPI.get().then(prefs => {
      if (Array.isArray(prefs.pd_projects_columns) && prefs.pd_projects_columns.length) {
        setVisibleProjectCols(prefs.pd_projects_columns);
      }
    }).catch(() => {});
  }, []);

  const toggleProjectCol = (key) => {
    setVisibleProjectCols(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      preferencesAPI.set('pd_projects_columns', next).catch(() => {});
      return next;
    });
  };
  const isProjectColVisible = (key) => visibleProjectCols.includes(key);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const partnersData = await partnersAPI.getAll();
      setPartners(partnersData);

      try {
        const usersData = await usersAPI.getAll();
        setUsers(usersData);
      } catch (e) {
        // users may not be accessible for all roles; silently ignore
      }
      
      const filters = { limit: 10000 };
      if (isPM() && user?.id) {
        filters.pm_id = user.id;
      }
      
      const projectsData = await trainingRequestsAPI.getAll(filters);
      setProjects(projectsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load training projects');
    } finally {
      setLoading(false);
    }
  }, [isPM, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getPdDateRange = () => {
    if (pdFiscalYear !== 'all') return { start: null, end: null };
    const now = new Date();
    if (pdTimePeriod === 'this_month') {
      const y = now.getFullYear(), m = String(now.getMonth() + 1).padStart(2, '0');
      return { start: `${y}-${m}-01`, end: `${y}-${m}-31` };
    }
    if (pdTimePeriod === 'this_year') {
      return { start: `${now.getFullYear()}-01-01`, end: `${now.getFullYear()}-12-31` };
    }
    if (/^\d{4}$/.test(pdTimePeriod)) {
      return { start: `${pdTimePeriod}-01-01`, end: `${pdTimePeriod}-12-31` };
    }
    return { start: null, end: null };
  };

  // The comparable "previous period" for the current PD filter — null when there isn't one (All Time)
  const getPreviousPeriodParams = () => {
    if (pdFiscalYear !== 'all') {
      const fyNum = parseInt(pdFiscalYear.slice(2), 10);
      return { fiscalYear: `FY${String(fyNum - 1).padStart(2, '0')}`, start: null, end: null };
    }
    const now = new Date();
    if (pdTimePeriod === 'this_month') {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0');
      const lastDay = new Date(y, d.getMonth() + 1, 0).getDate();
      return { fiscalYear: null, start: `${y}-${m}-01`, end: `${y}-${m}-${String(lastDay).padStart(2, '0')}` };
    }
    if (pdTimePeriod === 'this_year') {
      const y = now.getFullYear() - 1;
      return { fiscalYear: null, start: `${y}-01-01`, end: `${y}-12-31` };
    }
    if (/^\d{4}$/.test(pdTimePeriod)) {
      const y = parseInt(pdTimePeriod, 10) - 1;
      return { fiscalYear: null, start: `${y}-01-01`, end: `${y}-12-31` };
    }
    return null; // "All Time" has no natural predecessor
  };

  const loadAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const { start, end } = getPdDateRange();
      const prevParams = getPreviousPeriodParams();
      const [data, prevData] = await Promise.all([
        analyticsAPI.getDashboard([], null, start, end, 'private_deliveries', pdFiscalYear !== 'all' ? pdFiscalYear : null),
        prevParams
          ? analyticsAPI.getDashboard([], null, prevParams.start, prevParams.end, 'private_deliveries', prevParams.fiscalYear).catch(() => null)
          : Promise.resolve(null),
      ]);
      setAnalytics(data);
      setPreviousAnalyticsSummary(prevData?.summary || null);

      // PO Compliance Alerts, scoped to Private Delivery project IDs only
      try {
        const allAlerts = await purchaseOrdersAPI.getComplianceAlerts();
        const privateDeliveryIds = new Set(projects.map(p => p.id));
        setPoAlerts(allAlerts.filter(a => privateDeliveryIds.has(a.project_id)));
      } catch {
        // PO alerts are supplementary; ignore failures
      }
    } catch {
      toast.error('Failed to load dashboard analytics');
    } finally {
      setAnalyticsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, pdTimePeriod, pdFiscalYear]);

  useEffect(() => {
    if (activeTab === 'dashboard') loadAnalytics();
    setDrillThrough(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pdTimePeriod, pdFiscalYear]);

  const loadCalendarSessions = useCallback(async () => {
    try {
      setCalendarLoading(true);
      const allSessions = await sessionsAPI.getAll();
      const projectIds = new Set(projects.map(p => p.id));
      setCalendarSessions(allSessions.filter(s => projectIds.has(s.training_project_id)));
      setCalendarLoaded(true);
    } catch {
      toast.error('Failed to load calendar sessions');
    } finally {
      setCalendarLoading(false);
    }
  }, [projects]);

  useEffect(() => {
    if ((activeTab === 'projects' || activeTab === 'calendar' || activeTab === 'trainers') && !calendarLoaded && projects.length > 0) loadCalendarSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, projects.length]);

  useEffect(() => {
    if (activeTab === 'trainers' && !pdTrainersLoaded) {
      setPdTrainersLoading(true);
      trainersAPI.getAll()
        .then(data => { setPdTrainersAll(data); setPdTrainersLoaded(true); })
        .catch(() => toast.error('Failed to load trainers'))
        .finally(() => setPdTrainersLoading(false));
    }
  }, [activeTab, pdTrainersLoaded]);

  const loadPdMonthlyConsumption = useCallback(async () => {
    try {
      const data = await purchaseOrdersAPI.getMonthlyConsumption({ project_type: 'private_deliveries' });
      setPdMonthlyConsumption(data);
    } catch {
      // supplementary widget; ignore failures
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard') loadPdMonthlyConsumption();
  }, [activeTab, loadPdMonthlyConsumption]);

  const projectStatusMap = useMemo(
    () => Object.fromEntries(projects.map(p => [p.id, p.status])),
    [projects]
  );
  const projectById = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);

  const jumpToFilteredProjects = (filterKey, value) => {
    setColFilters(prev => ({ ...prev, [filterKey]: new Set([value]) }));
    setActiveTab('projects');
  };

  // ─── Dashboard Drill-Through (inline, stays within the Dashboard tab) ──────
  const PROJECT_DRILL_COLUMNS = [
    { key: 'title', label: 'Project' }, { key: 'partner_name', label: 'Partner' },
    { key: 'requestor_name', label: 'Requestor' }, { key: 'pm_name', label: 'PM' },
    { key: 'status_label', label: 'Status' },
  ];
  const SESSION_DRILL_COLUMNS = [
    { key: 'title', label: 'Session' }, { key: 'project_title', label: 'Project' },
    { key: 'date_label', label: 'Date' }, { key: 'session_type', label: 'Type' }, { key: 'status', label: 'Status' },
  ];
  const LEARNERS_DRILL_COLUMNS = [
    { key: 'project_title', label: 'Project' }, { key: 'learners', label: 'Learners' }, { key: 'source', label: 'Counted From' },
  ];
  const FEEDBACK_DRILL_COLUMNS = [
    { key: 'title', label: 'Session' }, { key: 'project_title', label: 'Project' },
    { key: 'trainer_name', label: 'Trainer' }, { key: 'course_title', label: 'Course' }, { key: 'avg_rating', label: 'Rating' },
  ];

  const mapProjectDrillRows = (list) => list.map(p => ({
    id: p.id,
    title: p.title,
    partner_name: getPartnerName(p.partner_id),
    requestor_name: p.requestor_name || '—',
    pm_name: p.pm_name || getPMName(p.pm_id),
    status_label: STATUS_VARIANTS[p.status]?.label || p.status,
  }));

  const openProjectsDrill = (title, list) => {
    setDrillThrough({ title, columns: PROJECT_DRILL_COLUMNS, rows: mapProjectDrillRows(list), type: 'projects' });
  };

  const openSessionsDrill = (title, list) => {
    const rows = list.map(s => ({
      id: s.id, project_id: s.project_id, title: s.title, project_title: s.project_title,
      date_label: s.session_date ? format(new Date(s.session_date), 'MMM d, yyyy') : '—',
      session_type: s.session_type, status: s.status, trainer_name: s.trainer_name || '—',
      course_title: s.course_title || '—', avg_rating: s.avg_rating != null ? `${s.avg_rating}/5` : '—',
    }));
    setDrillThrough({ title, columns: title === 'Avg Feedback' ? FEEDBACK_DRILL_COLUMNS : SESSION_DRILL_COLUMNS, rows, type: 'sessions' });
  };

  const openLearnersDrill = () => {
    const rows = (analytics.learners_breakdown || []).map(l => ({
      id: l.project_id, project_id: l.project_id, project_title: l.project_title, learners: l.learners,
      source: l.source === 'sessions' ? 'Session attendance' : 'Project total',
    }));
    setDrillThrough({ title: 'Learners Trained', columns: LEARNERS_DRILL_COLUMNS, rows, type: 'learners' });
  };

  const openFeedbackHistoryDrill = (type, key) => {
    const list = analytics.matching_sessions || [];
    const matches = list.filter(s => {
      if (s.avg_rating == null && !s.verbatims) return false;
      if (type === 'trainer') return s.trainer_name === key;
      if (type === 'course') return (s.course_title || 'Unknown') === key;
      return (s.partner_name || 'Unknown') === key;
    });
    const rows = matches.map(s => ({
      id: s.id, project_id: s.project_id, title: s.title, project_title: s.project_title,
      trainer_name: s.trainer_name || '—', course_title: s.course_title || '—',
      rating_label: s.avg_rating != null ? `${s.avg_rating}/5` : '—', verbatims: s.verbatims || '—',
      pm_followup_note: s.pm_followup_note || '', pm_followup_by: s.pm_followup_by, pm_followup_at: s.pm_followup_at,
    }));
    setDrillThrough({ title: `Feedback History — ${key}`, rows, type: 'feedback' });
  };

  const handleSaveFollowupNote = async (row, noteText) => {
    try {
      await feedbackAPI.updateSessionFeedback(row.id, {
        pm_followup_note: noteText,
        pm_followup_by: user?.name || 'PM',
        pm_followup_at: new Date().toISOString(),
      });
      setDrillThrough(prev => prev && ({
        ...prev,
        rows: prev.rows.map(r => r.id === row.id
          ? { ...r, pm_followup_note: noteText, pm_followup_by: user?.name || 'PM', pm_followup_at: new Date().toISOString() }
          : r),
      }));
      toast.success('Follow-up note saved');
    } catch {
      toast.error('Failed to save follow-up note');
    }
  };

  const handleDrillRowClick = (row) => {
    const pid = row.project_id || row.id;
    if (pid) navigate(`/training-projects/${pid}`);
  };

  const handleExportDashboardCSV = () => {
    if (!analytics) return;
    const filterLabel = pdFiscalYear !== 'all' ? `Fiscal Year: ${pdFiscalYear}` : `Period: ${pdTimePeriod === 'all' ? 'All Time' : pdTimePeriod}`;
    const headers = ['Project', 'Partner', 'Requestor', 'Project Manager', 'Status', 'Session Date'];
    const escape = (v) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = (analytics.recent_activity || []).map(p => [
      p.title, getPartnerName(p.partner_id), p.requestor_name || '', p.pm_name || getPMName(p.pm_id),
      STATUS_VARIANTS[p.status]?.label || p.status, p.preferred_dates || p.request_date || '',
    ].map(escape).join(','));
    const csv = [`# Private Deliveries Dashboard Export — ${filterLabel}`, headers.map(escape).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pd_dashboard_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this training project?')) {
      return;
    }
    
    try {
      await trainingRequestsAPI.delete(id);
      toast.success('Training project deleted');
      loadData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete training project');
    }
  };

  const getPartnerName = (partnerId) => {
    return partners.find(p => p.id === partnerId)?.name || 'Unknown';
  };

  const getPMName = (pmId) => {
    const pm = users.find(u => u.id === pmId);
    return pm ? pm.name : '—';
  };

  // Project metadata lookup (partner/requestor/pm/status) for Upcoming Deliveries drill-down
  const projectMetaMap = useMemo(() => {
    const map = {};
    (analytics?.recent_activity || []).forEach(p => {
      map[p.id] = {
        partner_name: getPartnerName(p.partner_id),
        requestor_name: p.requestor_name || '—',
        pm_name: p.pm_name || getPMName(p.pm_id),
        status: STATUS_VARIANTS[p.status]?.label || p.status,
      };
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, partners, users]);

  // Unique option lists for each column's filter popover, derived from loaded projects
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const projectOptions = useMemo(() => [...new Set(projects.map(p => p.title))].sort()
    .map(v => ({ value: v, label: v })), [projects]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const partnerOptions = useMemo(() => [...new Set(projects.map(p => getPartnerName(p.partner_id)))].sort()
    .map(v => ({ value: v, label: v })), [projects, partners]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const requestorOptions = useMemo(() => [...new Set(projects.map(p => p.requestor_name).filter(Boolean))].sort()
    .map(v => ({ value: v, label: v })), [projects]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pmOptions = useMemo(() => [...new Set(projects.map(p => p.pm_name || getPMName(p.pm_id)))].sort()
    .map(v => ({ value: v, label: v })), [projects, users]);
  const statusOptions = useMemo(() => Object.entries(STATUS_VARIANTS).map(([key, { label }]) => ({ value: key, label })), []);

  const setColFilter = (key, valuesSet) => setColFilters(prev => ({ ...prev, [key]: valuesSet }));

  const filteredProjects = projects.filter(proj => {
    if (search) {
      const searchLower = search.toLowerCase();
      const matchesSearch =
        proj.title.toLowerCase().includes(searchLower) ||
        proj.requestor_name?.toLowerCase().includes(searchLower) ||
        proj.requestor_email?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    if (colFilters.project.size > 0 && !colFilters.project.has(proj.title)) return false;
    if (colFilters.partner.size > 0 && !colFilters.partner.has(getPartnerName(proj.partner_id))) return false;
    if (colFilters.requestor.size > 0 && !colFilters.requestor.has(proj.requestor_name)) return false;
    if (colFilters.pm.size > 0 && !colFilters.pm.has(proj.pm_name || getPMName(proj.pm_id))) return false;
    if (colFilters.status.size > 0 && !colFilters.status.has(proj.status)) return false;
    if (dateRange.from || dateRange.to) {
      const rawDate = proj.preferred_dates || proj.request_date || '';
      const d = rawDate.slice(0, 10);
      if (dateRange.from && d < dateRange.from) return false;
      if (dateRange.to && d > dateRange.to) return false;
    }
    return true;
  });

  const hasActiveFilters = !!search || !!dateRange.from || !!dateRange.to ||
    Object.values(colFilters).some(s => s.size > 0);

  // Pagination (30/page). Reset to first page whenever filters/search change.
  useEffect(() => { setProjPage(1); }, [search, colFilters, dateRange]);
  const projTotal = filteredProjects.length;
  const pagedProjects = filteredProjects.slice((projPage - 1) * PAGE_SIZE, projPage * PAGE_SIZE);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(filteredProjects.map(p => p.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const handleBulkCopy = async () => {
    const selected = filteredProjects.filter(p => selectedIds.has(p.id));
    setBulkLoading(true);
    let copied = 0;
    for (const proj of selected) {
      try {
        const payload = {
          partner_id: proj.partner_id,
          title: `Copy of ${proj.title}`,
          description: proj.description || '',
          requestor_name: proj.requestor_name || '',
          requestor_email: proj.requestor_email || '',
          request_date: new Date().toISOString().split('T')[0],
          delivery_mode: proj.delivery_mode || 'virtual',
          preferred_dates: proj.preferred_dates || '',
          timezone: proj.timezone || 'UTC',
          location: proj.location || '',
          expected_attendees: proj.expected_attendees || 0,
          tsp_id: proj.tsp_id || null,
          notes: proj.notes || '',
        };
        await trainingRequestsAPI.create(payload, user.id);
        copied++;
      } catch (err) {
        console.error('Failed to copy project:', err);
      }
    }
    setBulkLoading(false);
    setSelectedIds(new Set());
    if (copied > 0) {
      toast.success(`Copied ${copied} project(s) successfully`);
      loadData();
    }
  };

  const handleBulkStatusChange = async () => {
    if (!bulkStatusValue) return;
    setBulkLoading(true);
    let updated = 0;
    for (const id of selectedIds) {
      try {
        await trainingRequestsAPI.updateStatus(id, bulkStatusValue);
        updated++;
      } catch (err) {
        console.error('Failed to update status:', err);
      }
    }
    setBulkLoading(false);
    setBulkStatusDialogOpen(false);
    setSelectedIds(new Set());
    if (updated > 0) {
      toast.success(`Updated status for ${updated} project(s)`);
      loadData();
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="training-projects-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Private Deliveries</h1>
          <p className="text-muted-foreground">
            Manage and track partner training delivery projects
          </p>
        </div>
        <Button 
          onClick={() => navigate('/training-projects/new')}
          data-testid="new-project-button"
          className="bg-lime text-lime-foreground hover:bg-lime/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Project
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <LayoutDashboard className="w-4 h-4 mr-1.5" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
          <TabsTrigger value="all-sessions" data-testid="tab-all-sessions">
            <CalendarDays className="w-4 h-4 mr-1.5" />
            All Sessions
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">
            <CalendarRange className="w-4 h-4 mr-1.5" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="trainers" data-testid="tab-trainers">
            <Users className="w-4 h-4 mr-1.5" />
            Trainers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="space-y-6 mt-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search projects..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
                <ColumnsPicker
                  columns={[...PROJECT_CORE_COLUMNS, ...customProjectColumns]}
                  visibleKeys={visibleProjectCols}
                  onToggle={toggleProjectCol}
                  testId="pd-projects-columns"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                <Filter className="w-3 h-3" />
                Tip: click any column header below (Project, Partner, Requestor, Project Manager, Session Date, Status) to filter by that field
              </p>
            </CardContent>
          </Card>

      <Card>
        <CardContent className="pt-6">
          {/* Bulk Action Bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 mb-3 p-3 bg-lime/10 border border-lime/30 rounded-lg">
              <CheckSquare className="w-4 h-4 text-lime-700" />
              <span className="text-sm font-medium text-lime-800">{selectedIds.size} project{selectedIds.size > 1 ? 's' : ''} selected</span>
              <div className="flex gap-2 ml-auto">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBulkCopy}
                  disabled={bulkLoading}
                  data-testid="bulk-copy-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Selected
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setBulkStatusValue(''); setBulkStatusDialogOpen(true); }}
                  disabled={bulkLoading}
                  data-testid="bulk-status-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Change Status
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear-btn">
                  Clear
                </Button>
              </div>
            </div>
          )}
          <div className="mb-3">
            <ActiveFiltersBar
              filters={[
                { key: 'project', label: 'Project', set: colFilters.project, options: projectOptions.map(v => ({ value: v, label: v })), onChange: (s) => setColFilter('project', s) },
                { key: 'partner', label: 'Partner', set: colFilters.partner, options: partnerOptions.map(v => ({ value: v, label: v })), onChange: (s) => setColFilter('partner', s) },
                { key: 'requestor', label: 'Requestor', set: colFilters.requestor, options: requestorOptions.map(v => ({ value: v, label: v })), onChange: (s) => setColFilter('requestor', s) },
                { key: 'pm', label: 'Project Manager', set: colFilters.pm, options: pmOptions.map(v => ({ value: v, label: v })), onChange: (s) => setColFilter('pm', s) },
                { key: 'status', label: 'Status', set: colFilters.status, options: statusOptions, onChange: (s) => setColFilter('status', s) },
              ]}
              search={search}
              onClearSearch={() => setSearch('')}
              testId="pd-active-filters"
            />
          </div>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : filteredProjects.length > 0 ? (
            <Table containerClassName="max-h-[calc(100vh-300px)]">
              <TableHeader sticky>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={filteredProjects.length > 0 && selectedIds.size === filteredProjects.length}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead>
                    <ColumnFilterPopover label="Project" options={projectOptions} selected={colFilters.project} onChange={(s) => setColFilter('project', s)} />
                  </TableHead>
                  {isProjectColVisible('partner') && (
                    <TableHead>
                      <ColumnFilterPopover label="Partner" options={partnerOptions} selected={colFilters.partner} onChange={(s) => setColFilter('partner', s)} />
                    </TableHead>
                  )}
                  {isProjectColVisible('requestor') && (
                    <TableHead>
                      <ColumnFilterPopover label="Requestor" options={requestorOptions} selected={colFilters.requestor} onChange={(s) => setColFilter('requestor', s)} />
                    </TableHead>
                  )}
                  {isProjectColVisible('pm') && (
                    <TableHead>
                      <ColumnFilterPopover label="Project Manager" options={pmOptions} selected={colFilters.pm} onChange={(s) => setColFilter('pm', s)} />
                    </TableHead>
                  )}
                  {isProjectColVisible('sessions') && <TableHead>No. of Sessions</TableHead>}
                  {isProjectColVisible('amount_invoiced') && <TableHead>Amount Invoiced</TableHead>}
                  {isProjectColVisible('session_date') && (
                    <TableHead>
                      <DateRangeFilterPopover label="Session Date" range={dateRange} onChange={setDateRange} />
                    </TableHead>
                  )}
                  {isProjectColVisible('status') && (
                    <TableHead>
                      <ColumnFilterPopover label="Status" options={statusOptions} selected={colFilters.status} onChange={(s) => setColFilter('status', s)} />
                    </TableHead>
                  )}
                  {customProjectColumns.filter(c => isProjectColVisible(c.key)).map(c => (
                    <TableHead key={c.key}>{c.label}</TableHead>
                  ))}
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedProjects.map((proj) => (
                  <TableRow 
                    key={proj.id}
                    className="cursor-pointer hover:bg-muted/50"
                    data-testid={`project-row-${proj.id}`}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(proj.id)}
                        onCheckedChange={(checked) => handleSelectOne(proj.id, checked)}
                        data-testid={`select-project-${proj.id}`}
                      />
                    </TableCell>
                    <TableCell 
                      className="font-medium"
                      onClick={() => navigate(`/training-projects/${proj.id}`)}
                    >
                      <div className="flex items-center gap-2">
                        <FolderKanban className="w-4 h-4 text-lime" />
                        {proj.title}
                      </div>
                    </TableCell>
                    {isProjectColVisible('partner') && (
                      <TableCell onClick={() => navigate(`/training-projects/${proj.id}`)}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          {getPartnerName(proj.partner_id)}
                        </div>
                      </TableCell>
                    )}
                    {isProjectColVisible('requestor') && (
                      <TableCell onClick={() => navigate(`/training-projects/${proj.id}`)}>
                        <div>
                          <p className="text-sm">{proj.requestor_name}</p>
                          <p className="text-xs text-muted-foreground">{proj.requestor_email}</p>
                        </div>
                      </TableCell>
                    )}
                    {isProjectColVisible('pm') && (
                      <TableCell onClick={() => navigate(`/training-projects/${proj.id}`)}>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{proj.pm_name || getPMName(proj.pm_id)}</span>
                        </div>
                      </TableCell>
                    )}
                    {isProjectColVisible('sessions') && (
                      <TableCell onClick={() => navigate(`/training-projects/${proj.id}`)} data-testid={`project-session-count-${proj.id}`}>
                        <span className="text-sm">{sessionCountByProject[proj.id] || 0}</span>
                      </TableCell>
                    )}
                    {isProjectColVisible('amount_invoiced') && (
                      <TableCell onClick={() => navigate(`/training-projects/${proj.id}`)} data-testid={`project-amount-invoiced-${proj.id}`}>
                        <span className="text-sm">{amountInvoicedByProject[proj.id] ? `$${amountInvoicedByProject[proj.id].toLocaleString()}` : '—'}</span>
                      </TableCell>
                    )}
                    {isProjectColVisible('session_date') && (
                    <TableCell onClick={() => navigate(`/training-projects/${proj.id}`)}>
                      {(() => {
                        const rawDate = proj.preferred_dates || proj.request_date;
                        if (!rawDate) return <span className="text-muted-foreground">—</span>;

                        // Try to parse as ISO date
                        const parsed = new Date(rawDate);
                        const isValidDate = !isNaN(parsed.getTime()) && rawDate.match(/^\d{4}-\d{2}-\d{2}/);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        parsed.setHours(0, 0, 0, 0);

                        let colorClass = 'text-muted-foreground';
                        let bgClass = '';
                        let iconColor = 'text-muted-foreground';
                        let dotColor = '';

                        if (isValidDate) {
                          if (parsed.getTime() === today.getTime()) {
                            colorClass = 'text-amber-700 font-medium';
                            bgClass = 'bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5';
                            iconColor = 'text-amber-500';
                            dotColor = 'bg-amber-400';
                          } else if (parsed > today) {
                            colorClass = 'text-green-700 font-medium';
                            bgClass = 'bg-green-50 border border-green-200 rounded-md px-2 py-0.5';
                            iconColor = 'text-green-500';
                            dotColor = 'bg-green-400';
                          } else {
                            colorClass = 'text-red-700 font-medium';
                            bgClass = 'bg-red-50 border border-red-200 rounded-md px-2 py-0.5';
                            iconColor = 'text-red-500';
                            dotColor = 'bg-red-400';
                          }
                        }

                        const label = isValidDate
                          ? format(new Date(rawDate), 'MMM d, yyyy')
                          : rawDate;

                        return (
                          <div className={`inline-flex items-center gap-1.5 ${bgClass}`}>
                            {dotColor ? (
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
                            ) : (
                              <Calendar className={`w-3.5 h-3.5 flex-shrink-0 ${iconColor}`} />
                            )}
                            <span className={`text-sm ${colorClass}`}>{label}</span>
                          </div>
                        );
                      })()}
                    </TableCell>
                    )}
                    {isProjectColVisible('status') && (
                    <TableCell onClick={() => navigate(`/training-projects/${proj.id}`)}>
                      <Badge className={STATUS_VARIANTS[proj.status]?.className || ''}>
                        {proj.status === 'completed' && <Lock className="w-3 h-3 mr-1 inline" />}
                        {STATUS_VARIANTS[proj.status]?.label || proj.status}
                      </Badge>
                    </TableCell>
                    )}
                    {customProjectColumns.filter(c => isProjectColVisible(c.key)).map(c => (
                      <TableCell key={c.key} onClick={() => navigate(`/training-projects/${proj.id}`)}>
                        <span className="text-sm">{proj.custom_fields?.[c.taxonomyKey] || '—'}</span>
                      </TableCell>
                    ))}
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/training-projects/${proj.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/training-projects/${proj.id}/edit`)}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(proj.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <FolderKanban className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No training projects found</h3>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'Get started by creating your first training project'}
              </p>
              {!hasActiveFilters && (
                <Button onClick={() => navigate('/training-projects/new')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Training Project
                </Button>
              )}
            </div>
          )}
          <TablePagination page={projPage} total={projTotal} onPageChange={setProjPage} testId="pd-projects-pagination" itemLabel="projects" />
        </CardContent>
      </Card>
        </TabsContent>

        {/* All Sessions Tab — moved here from the left sidebar */}
        <TabsContent value="all-sessions" className="mt-4" data-testid="private-deliveries-all-sessions-tab">
          <AllSessionsPage embedded />
        </TabsContent>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-4 space-y-5" data-testid="private-deliveries-dashboard">
          {/* Filters — mirrors main Dashboard */}
          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" size="sm" onClick={handleExportDashboardCSV} data-testid="pd-dashboard-export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export Current View
            </Button>
            <Select
              value={pdTimePeriod}
              onValueChange={(v) => { setPdTimePeriod(v); if (v !== 'all') setPdFiscalYear('all'); }}
            >
              <SelectTrigger className="w-40" data-testid="pd-time-period-filter">
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
                {[2026, 2025, 2024, 2023].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FiscalYearSelect
              value={pdFiscalYear}
              onChange={(v) => { setPdFiscalYear(v); if (v !== 'all') setPdTimePeriod('all'); }}
              testId="pd-fiscal-year-filter"
            />
          </div>

          {analyticsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
              {[1,2,3,4].map(i => <div key={i} className="h-28 bg-muted animate-pulse rounded-xl" />)}
            </div>
          ) : analytics ? (
            <>
              {/* PO Compliance Alerts (scoped to Private Delivery projects) */}
              <POAlertBanners
                poAlerts={poAlerts}
                exhaustedPos={[]}
                onViewProject={(pid) => navigate(`/training-projects/${pid}`)}
                onViewAllAlerts={() => {}}
              />

              {/* Inline drill-through — appears here, replacing nothing, stays in this tab */}
              {drillThrough && drillThrough.type === 'feedback' && (
                <FeedbackHistoryPanel
                  title={drillThrough.title}
                  rows={drillThrough.rows}
                  onRowClick={handleDrillRowClick}
                  onSaveNote={handleSaveFollowupNote}
                  onClose={() => setDrillThrough(null)}
                  testId="pd-feedback-history"
                />
              )}
              {drillThrough && drillThrough.type !== 'feedback' && (
                <DashboardDrillThroughPanel
                  title={drillThrough.title}
                  columns={drillThrough.columns}
                  rows={drillThrough.rows}
                  onRowClick={handleDrillRowClick}
                  onClose={() => setDrillThrough(null)}
                  testId="pd-drill-through"
                />
              )}

              {/* KPI Cards — order matches main Dashboard: Training Projects | Sessions | Learners Trained | Avg Feedback */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card
                  className="border-l-4 border-l-lime cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openProjectsDrill('Training Projects', analytics.recent_activity || [])}
                  data-testid="pd-kpi-training-projects"
                >
                  <CardContent className="pt-5 pb-5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Training Projects</p>
                    <p className="text-2xl font-bold font-heading mt-1">{analytics.summary?.total_trainings || 0}</p>
                    <div className="mt-1 flex items-center gap-1 text-muted-foreground">
                      <FolderKanban className="w-3.5 h-3.5" />
                      <span className="text-xs">Private deliveries</span>
                    </div>
                    <div className="mt-1.5">
                      <KpiTrendBadge current={analytics.summary?.total_trainings || 0} previous={previousAnalyticsSummary?.total_trainings} testId="pd-kpi-trend-projects" />
                    </div>
                  </CardContent>
                </Card>
                <Card
                  className="border-l-4 border-l-violet-500 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openSessionsDrill('Sessions', analytics.matching_sessions || [])}
                  data-testid="pd-kpi-sessions"
                >
                  <CardContent className="pt-5 pb-5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Sessions</p>
                    <p className="text-2xl font-bold font-heading mt-1">{analytics.summary?.total_sessions || 0}</p>
                    <div className="mt-1 flex items-center gap-1 text-muted-foreground">
                      <CalendarDays className="w-3.5 h-3.5" />
                      <span className="text-xs">All session types</span>
                    </div>
                    <div className="mt-1.5">
                      <KpiTrendBadge current={analytics.summary?.total_sessions || 0} previous={previousAnalyticsSummary?.total_sessions} testId="pd-kpi-trend-sessions" />
                    </div>
                  </CardContent>
                </Card>
                <Card
                  className="border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={openLearnersDrill}
                  data-testid="pd-kpi-learners"
                >
                  <CardContent className="pt-5 pb-5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Learners Trained</p>
                    <p className="text-2xl font-bold font-heading mt-1">{analytics.summary?.total_learners_trained || 0}</p>
                    <div className="mt-1 flex items-center gap-1 text-muted-foreground">
                      <GraduationCap className="w-3.5 h-3.5" />
                      <span className="text-xs">Across all deliveries</span>
                    </div>
                    <div className="mt-1.5">
                      <KpiTrendBadge current={analytics.summary?.total_learners_trained || 0} previous={previousAnalyticsSummary?.total_learners_trained} testId="pd-kpi-trend-learners" />
                    </div>
                  </CardContent>
                </Card>
                <Card
                  className="border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openSessionsDrill('Avg Feedback', (analytics.matching_sessions || []).filter(s => s.avg_rating != null))}
                  data-testid="pd-kpi-avg-feedback"
                >
                  <CardContent className="pt-5 pb-5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Avg Feedback</p>
                    <p className="text-2xl font-bold font-heading mt-1">
                      {analytics.summary?.avg_feedback != null ? `${analytics.summary.avg_feedback}/5` : '—'}
                    </p>
                    <div className="mt-1 flex items-center gap-1 text-muted-foreground">
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                      <span className="text-xs">MTM average</span>
                    </div>
                    <div className="mt-1.5">
                      <KpiTrendBadge current={analytics.summary?.avg_feedback} previous={previousAnalyticsSummary?.avg_feedback} testId="pd-kpi-trend-feedback" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Upcoming Deliveries */}
              <UpcomingDeliveriesCard
                sessions={analytics.matching_sessions || []}
                projectMetaMap={projectMetaMap}
                windowWeeks={upcomingWindowWeeks}
                onWindowChange={setUpcomingWindowWeeks}
                onItemClick={(s) => navigate(`/training-projects/${s.project_id}`)}
                testId="pd-upcoming-deliveries"
              />

              {/* Project Status + Trainings by Partner (both drill-through on click) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <ProjectStatusPieChart
                  statusBreakdown={analytics.status_breakdown || {}}
                  onSliceClick={(status) => openProjectsDrill(
                    `Status: ${STATUS_VARIANTS[status]?.label || status}`,
                    (analytics.recent_activity || []).filter(p => p.status === status)
                  )}
                  testId="pd-chart-status"
                />
                <TrainingsByPartnerChart
                  partnerBreakdown={analytics.partner_breakdown || []}
                  onBarClick={(bar) => openProjectsDrill(
                    `Partner: ${bar.name}`,
                    (analytics.recent_activity || []).filter(p => p.partner_id === bar.partner_id)
                  )}
                  testId="pd-chart-partners"
                />
              </div>

              {/* Monthly Trend */}
              <MonthlyTrendChart monthlyTrend={analytics.monthly_session_trend || []} testId="pd-chart-monthly-trend" />

              {/* Feedback Trend Over Time */}
              <FeedbackTrendCard
                monthlyTrend={analytics.feedback_monthly_trend || []}
                byTrainer={analytics.feedback_by_trainer || []}
                byPartner={analytics.feedback_by_partner || []}
                byCourse={analytics.feedback_by_course || []}
                onDrill={openFeedbackHistoryDrill}
                testId="pd-feedback-trend"
              />

              {/* Attendance Fill Rate */}
              <AttendanceFillRateCard
                avgFillRate={analytics.summary?.avg_fill_rate}
                underAttendedSessions={analytics.under_attended_sessions || []}
                onRowClick={(s) => navigate(`/training-projects/${s.project_id}`)}
                testId="pd-fill-rate"
              />

              {/* Month-wise Consumed vs Invoiced Budget (scoped to Private Deliveries) */}
              <MonthlyConsumptionChart
                data={pdMonthlyConsumption}
                onRefresh={loadPdMonthlyConsumption}
                title="Private Deliveries Budget"
              />

              {/* Deliveries by Owner */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <OwnerBreakdownChart
                  title="By Project Manager"
                  data={(analytics.pm_breakdown || []).slice(0, 8).map(p => ({ name: p.pm_name, count: p.count }))}
                  color="#8B5CF6"
                  onBarClick={(row) => jumpToFilteredProjects('pm', row.name)}
                  testId="pd-chart-by-pm"
                />
                <OwnerBreakdownChart
                  title="By Requestor"
                  data={(analytics.requestor_breakdown || []).slice(0, 8).map(r => ({ name: r.requestor_name, count: r.count }))}
                  color="#F59E0B"
                  onBarClick={(row) => jumpToFilteredProjects('requestor', row.name)}
                  testId="pd-chart-by-requestor"
                />
              </div>

              {/* Delivery Mix */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base">Delivery Mix</CardTitle>
                  <CardDescription className="text-xs">Sessions by delivery format</CardDescription>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const types = analytics.summary?.sessions_by_type || {};
                    const data = [
                      { name: 'Virtual', value: types.virtual || 0, color: '#3B82F6' },
                      { name: 'In-Person', value: types.in_person || 0, color: '#10B981' },
                      { name: 'Hybrid', value: types.hybrid || 0, color: '#8B5CF6' },
                    ].filter(d => d.value > 0);
                    if (!data.length) return <p className="text-center text-muted-foreground py-8 text-sm">No session data</p>;
                    return (
                      <div className="flex items-center gap-6 max-w-md mx-auto">
                        <ResponsiveContainer width="50%" height={160}>
                          <PieChart>
                            <Pie data={data} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value">
                              {data.map((d, i) => <Cell key={i} fill={d.color} />)}
                            </Pie>
                            <RechartsTooltip formatter={(v, n) => [v, n]} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="flex-1 space-y-2.5">
                          {data.map(d => (
                            <div key={d.name} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                                <span className="text-sm">{d.name}</span>
                              </div>
                              <span className="font-semibold text-sm">{d.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-center py-16 text-muted-foreground">
              <LayoutDashboard className="w-12 h-12 mx-auto mb-4 opacity-40" />
              <p>Click the Dashboard tab to load analytics</p>
            </div>
          )}
        </TabsContent>

        {/* Trainers Tab (scoped to Private Deliveries) */}
        <TabsContent value="trainers" className="mt-4" data-testid="private-deliveries-trainers-tab">
          <PrivateDeliveryTrainersTab
            sessions={calendarSessions}
            trainers={pdTrainersAll}
            loading={(calendarLoading && !calendarLoaded) || (pdTrainersLoading && !pdTrainersLoaded)}
          />
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="mt-4" data-testid="private-deliveries-calendar-tab">
          {calendarLoading ? (
            <div className="h-96 bg-muted animate-pulse rounded-xl" />
          ) : (
            <ProjectStatusCalendar
              items={calendarSessions.map(s => {
                const proj = projectById[s.training_project_id] || {};
                return {
                  id: s.id,
                  date: s.session_date,
                  endDate: s.end_date,
                  startTime: s.start_time,
                  endTime: s.end_time,
                  title: s.title,
                  trainers: sessionTrainers(s),
                  pm: proj.pm_name || getPMName(proj.pm_id),
                  client: proj.partner_id ? getPartnerName(proj.partner_id) : undefined,
                  statusKey: projectStatusMap[s.training_project_id] || 'requested',
                  training_project_id: s.training_project_id,
                };
              })}
              onItemClick={(it) => navigate(`/training-projects/${it.training_project_id}`)}
              testId="pd-calendar-view"
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Bulk Status Change Dialog */}
      <Dialog open={bulkStatusDialogOpen} onOpenChange={setBulkStatusDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Status</DialogTitle>
            <DialogDescription>
              Select a new status for {selectedIds.size} selected project{selectedIds.size > 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Select value={bulkStatusValue} onValueChange={setBulkStatusValue}>
              <SelectTrigger data-testid="bulk-status-select">
                <SelectValue placeholder="Select status..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_VARIANTS).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setBulkStatusDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleBulkStatusChange}
                disabled={!bulkStatusValue || bulkLoading}
                className="bg-lime text-lime-foreground hover:bg-lime/90"
                data-testid="bulk-status-confirm-btn"
              >
                {bulkLoading ? 'Updating...' : 'Update Status'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingProjectsPage;
